import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { Sentry } from '@shared/sentry';
import { Analytics } from '@shared/analytics';
import { vueQueryLib } from '@shared/lib/vue-query-lib';

import { TownsToAPI } from '../api';
import { townsToConfig } from '../config';

export const useTownsToQuery = (
  countryToId: Ref<number | undefined>,
  townFromId: Ref<number | undefined>,
) => {
  const isQueryEnabled = computed(
    () => countryToId.value !== undefined && townFromId.value !== undefined,
  );

  const townsToQuery = useQuery({
    queryKey: ['town-to-query', countryToId],
    queryFn: async () => {
      const response = await TownsToAPI.getTownsTo(
        countryToId.value as number,
        townFromId.value as number,
      );
      return response.data;
    },
    staleTime: Infinity, // Static data from admin, so it can't become obsolete during a user session
    enabled: isQueryEnabled,
  });

  vueQueryLib.withOnSuccess({
    query: townsToQuery,
    onSuccess() {
      Analytics.logEvent({ event: townsToConfig.analyticsEvents.SUCCESS });
    },
  });

  vueQueryLib.withOnError({
    query: townsToQuery,
    onError(error) {
      Analytics.logEvent({ event: townsToConfig.analyticsEvents.ERROR });

      Sentry.captureExWithCause('ERR_FAILED_TO_FETCH_TOWNS_TO', {
        cause: error,
      });
    },
  });

  return townsToQuery;
};
